import { type JSX, useCallback, useEffect, useState } from "react";

import type { Environment } from "../api/bench";
import {
  type ActivityRow,
  type CallLogEntry,
  fetchActivity,
  fetchCallLog,
} from "../api/stats";
import { MultiSelect } from "./MultiSelect";

/** The nav entry this panel answers to. */
export const ACTIVITY_VIEW = "__activity__";

const MEMORY_TYPES = [
  "requirement",
  "fact",
  "decision",
  "event",
  "artifact",
  "preference",
  "summary",
] as const;

const PERIODS = [
  { key: 0, label: "All time" },
  { key: 1, label: "Today" },
  { key: 7, label: "Last 7 days" },
  { key: 30, label: "Last 30 days" },
] as const;

type Tab = "platform" | "calls";

interface ActivityListProps {
  environment: Environment;
  /** Project to start on, set when arriving from the dashboard's Inspect. */
  initialProject: string | null;
}

/**
 * Everything that happened, from the two places it can honestly be read.
 *
 * *Platform activity* is what the service recorded — every durable memory, with its type, status,
 * writing agent and timestamps, filtered by the service rather than by this component so a
 * filtered page is a filtered query and not a filtered subset of one page.
 *
 * *Calls* is what this bench did — including the reads and the failures, which leave no trace in
 * the graph at all. The service keeps no API log, so for those this is the only record there is,
 * and it lasts as long as the session.
 */
export function ActivityList({ environment, initialProject }: ActivityListProps): JSX.Element {
  const [tab, setTab] = useState<Tab>("platform");

  return (
    <section className="panel">
      <header className="panel__header">
        <div>
          <h2>Activity</h2>
          <p className="muted">
            What the platform recorded, and what this session did. The Memory Service keeps no
            call log, so reads and failures exist only in the second tab.
          </p>
        </div>
      </header>

      <div className="tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "platform"}
          className={tab === "platform" ? "tabs__tab tabs__tab--active" : "tabs__tab"}
          onClick={() => setTab("platform")}
        >
          Platform activity
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "calls"}
          className={tab === "calls" ? "tabs__tab tabs__tab--active" : "tabs__tab"}
          onClick={() => setTab("calls")}
        >
          Calls this session
        </button>
      </div>

      {tab === "platform" ? (
        <PlatformActivity environment={environment} initialProject={initialProject} />
      ) : (
        <CallLog />
      )}
    </section>
  );
}

function PlatformActivity({
  environment,
  initialProject,
}: {
  environment: Environment;
  initialProject: string | null;
}): JSX.Element {
  // Empty means every known project, matching how the query is phrased.
  const [projects, setProjects] = useState<string[]>(initialProject ? [initialProject] : []);
  const [days, setDays] = useState(0);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [minImportance, setMinImportance] = useState("");
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [cursors, setCursors] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (append: Record<string, string> | null): Promise<void> => {
      setLoading(true);
      setProblem(null);
      try {
        const page = await fetchActivity({
          ...(projects.length ? { projects } : {}),
          ...(days ? { days } : {}),
          ...(type ? { memory_type: type } : {}),
          ...(status ? { memory_status: status } : {}),
          ...(minImportance ? { min_importance: Number(minImportance) } : {}),
          ...(append ? { cursors: append } : {}),
          limit: 50,
        });
        // Several projects mean several failures, so they are reported together rather than
        // letting one unreadable project look like an empty result.
        setProblem(page.problems.length ? page.problems.join(" · ") : null);
        // Appending when following cursors, replacing when the filters changed: otherwise
        // "Load more" would silently discard everything above it.
        setRows((current) => (append ? [...current, ...page.rows] : page.rows));
        setCursors(page.cursors);
      } catch (cause) {
        setProblem(cause instanceof Error ? cause.message : String(cause));
      } finally {
        setLoading(false);
      }
    },
    [projects, days, type, status, minImportance],
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  const offered = [
    ...new Set([...environment.default_projects, ...(initialProject ? [initialProject] : [])]),
  ];

  return (
    <>
      <div className="filters">
        <label>
          <span>Period</span>
          <select value={days} onChange={(event) => setDays(Number(event.target.value))}>
            {PERIODS.map((period) => (
              <option key={period.key} value={period.key}>
                {period.label}
              </option>
            ))}
          </select>
        </label>

        <MultiSelect
          label="Projects"
          options={offered}
          selected={projects}
          onChange={setProjects}
          allLabel={`All ${offered.length} projects`}
          searchPlaceholder="Find a project…"
        />

        <label>
          <span>Type</span>
          <select value={type} onChange={(event) => setType(event.target.value)}>
            <option value="">any</option>
            {MEMORY_TYPES.map((entry) => (
              <option key={entry} value={entry}>
                {entry}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Status</span>
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">any</option>
            <option value="active">active</option>
            <option value="superseded">superseded</option>
          </select>
        </label>

        <label>
          <span>Min importance</span>
          <input
            type="number"
            min="0"
            max="1"
            step="0.1"
            value={minImportance}
            placeholder="any"
            onChange={(event) => setMinImportance(event.target.value)}
          />
        </label>
      </div>

      {problem && (
        <div className="notice notice--error">
          <strong>Could not read activity.</strong> {problem}
        </div>
      )}

      {!problem && rows.length === 0 && !loading && (
        <p className="muted">Nothing recorded for this slice.</p>
      )}

      {rows.length > 0 && (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Type</th>
                <th>Status</th>
                <th>Agent</th>
                <th>Importance</th>
                <th>What</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="nowrap">{stamp(row.created_at)}</td>
                  <td>{row.type}</td>
                  <td>
                    {row.status}
                    {row.embedding_status && row.embedding_status !== "ready" && (
                      <>
                        {" "}
                        <span
                          className="flag flag--warn"
                          title="Not findable by recall until the embedding worker catches up"
                        >
                          {row.embedding_status}
                        </span>
                      </>
                    )}
                  </td>
                  <td>{row.agent_id ?? <span className="muted">unattributed</span>}</td>
                  <td>{row.importance ?? "—"}</td>
                  <td title={row.id}>{row.summary ?? row.subject ?? row.id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="panel__actions">
        {Object.keys(cursors).length > 0 ? (
          <>
            <button type="button" onClick={() => void load(cursors)} disabled={loading}>
              {loading ? "Loading…" : "Load more"}
            </button>
            <span className="muted">
              {rows.length} shown; more in{" "}
              {Object.keys(cursors).length === 1
                ? Object.keys(cursors)[0]
                : `${Object.keys(cursors).length} projects`}
            </span>
          </>
        ) : (
          rows.length > 0 && <span className="muted">{rows.length} shown; no more pages.</span>
        )}
      </div>
    </>
  );
}

function CallLog(): JSX.Element {
  const [calls, setCalls] = useState<CallLogEntry[]>([]);
  const [capacity, setCapacity] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);
  const [onlyFailures, setOnlyFailures] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setProblem(null);
    try {
      const log = await fetchCallLog();
      setCalls(log.calls);
      setCapacity(log.capacity);
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = onlyFailures ? calls.filter((call) => !call.ok) : calls;

  return (
    <>
      <div className="filters">
        <label className="filters__check">
          <input
            type="checkbox"
            checked={onlyFailures}
            onChange={(event) => setOnlyFailures(event.target.checked)}
          />
          Failures only
        </label>
        <button type="button" onClick={() => void load()} disabled={loading}>
          Refresh
        </button>
        <span className="muted">
          {calls.length} of the last {capacity} kept for this session
        </span>
      </div>

      {problem && (
        <div className="notice notice--error">
          <strong>Could not read the call log.</strong> {problem}
        </div>
      )}

      {shown.length === 0 ? (
        <p className="muted">
          {calls.length === 0
            ? "Nothing run yet in this session. Every call you make appears here."
            : "No failures in this session."}
        </p>
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Operation</th>
                <th>Result</th>
                <th>Took</th>
                <th>Identity</th>
                <th>Request id</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((call) => (
                <tr key={call.call_id} className={call.ok ? "" : "row--failed"}>
                  <td className="nowrap">{stamp(call.started_at)}</td>
                  <td title={`${call.method} ${call.url}`}>{call.task_label}</td>
                  <td>
                    <span className={`flag flag--${call.ok ? "on" : "danger"}`}>{call.status}</span>
                    {call.problem_title && <> {call.problem_title}</>}
                  </td>
                  <td>{call.duration_ms} ms</td>
                  <td>{call.profile_used ?? <span className="muted">none</span>}</td>
                  <td>
                    {call.request_id ? (
                      <CopyableId value={call.request_id} />
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/** The correlation id is the one thing a bug report needs, so it is one click to take. */
function CopyableId({ value }: { value: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="link-button"
      title={value}
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? "copied" : `${value.slice(0, 8)}…`}
    </button>
  );
}

function stamp(value: string | null): string {
  if (!value) {
    return "—";
  }
  return value.slice(0, 19).replace("T", " ");
}
